// ============================================================
//  TabBar — SAP-style bottom task bar for open pages
//  Shows all open tabs as pills with close button + right-click menu.
// ============================================================
import React, { useState, useRef, useEffect } from "react";
import { useOpenTabs } from "./OpenTabsContext";

const TabBar: React.FC = () => {
  const { tabs, activePath, closeTab, switchTab, closeAllTabs, closeOtherTabs } = useOpenTabs();
  const [menu, setMenu] = useState<{ x: number; y: number; path: string } | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to active tab
  useEffect(() => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current.querySelector(`[data-tab-active="true"]`);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activePath]);

  // Dismiss right-click menu on outside click
  useEffect(() => {
    if (!menu) return;
    const h = () => setMenu(null);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [menu]);

  if (tabs.length === 0) return null;

  return (
    <>
      {/* Animations */}
      <style>{`
        @keyframes tabSlideIn {
          from { opacity: 0; transform: translateY(8px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes activeTabGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.5); }
          50%      { box-shadow: 0 0 0 6px rgba(99,102,241,0); }
        }
        .erp-tab {
          animation: tabSlideIn 0.28s cubic-bezier(.34,1.56,.64,1);
        }
        .erp-tab:hover .erp-tab-close {
          opacity: 1 !important;
        }
        .erp-tab-bar-scroll::-webkit-scrollbar { height: 4px; }
        .erp-tab-bar-scroll::-webkit-scrollbar-thumb {
          background: var(--erp-primary);
          border-radius: 4px;
        }
        .erp-tab-bar-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      <div style={{
        position: "fixed", left: 0, right: 0, bottom: 0,
        height: "var(--erp-tabbar-height)",
        background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(12,18,35,1) 100%)",
        backdropFilter: "blur(14px)",
        borderTop: "1px solid rgba(148,163,184,0.18)",
        boxShadow: "0 -6px 22px rgba(0,0,0,0.35), 0 -1px 0 rgba(255,255,255,0.04) inset",
        display: "flex", alignItems: "center",
        zIndex: 1100,
        paddingLeft: 6,
        paddingRight: 6,
      }}>

        {/* Windows-style label */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "0 12px", fontSize: 10, fontWeight: 700,
          color: "#64748B", letterSpacing: "1px", textTransform: "uppercase",
          borderRight: "1px solid rgba(148,163,184,0.18)",
          height: 24, marginRight: 6,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          OPEN
        </div>

        {/* Scrollable tab list */}
        <div
          ref={scrollerRef}
          className="erp-tab-bar-scroll"
          style={{
            flex: 1, display: "flex", alignItems: "center", gap: 4,
            overflowX: "auto", overflowY: "hidden", height: "100%",
            scrollbarWidth: "thin",
          }}>
          {tabs.map(tab => {
            const active = tab.path === activePath;
            return (
              <div
                key={tab.path}
                data-tab-active={active}
                className="erp-tab"
                onClick={() => !active && switchTab(tab.path)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, path: tab.path });
                }}
                onMouseDown={(e) => {
                  // Middle-click closes tab (like browser)
                  if (e.button === 1) { e.preventDefault(); closeTab(tab.path); }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  height: 28, padding: "0 10px 0 10px",
                  borderRadius: 6,
                  background: active
                    ? "rgba(29,78,216,0.28)"
                    : "rgba(30,41,59,0.55)",
                  border: active
                    ? "1px solid rgba(29,78,216,0.65)"
                    : "1px solid rgba(51,65,85,0.5)",
                  cursor: active ? "default" : "pointer",
                  fontSize: 12,
                  color: active ? "#F5F3FF" : "#94A3B8",
                  fontWeight: active ? 600 : 500,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  transition: "background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s",
                  position: "relative",
                  boxShadow: active
                    ? "0 2px 8px rgba(99,102,241,0.35), 0 0 0 1px rgba(139,92,246,0.22) inset"
                    : "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "rgba(51,65,85,0.75)";
                    e.currentTarget.style.color = "#E2E8F0";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "rgba(30,41,59,0.55)";
                    e.currentTarget.style.color = "#94A3B8";
                    e.currentTarget.style.transform = "translateY(0)";
                  }
                }}
              >
                {/* Active indicator dot */}
                {active && (
                  <span style={{
                    position: "absolute", top: -1, left: "50%", transform: "translateX(-50%)",
                    width: 28, height: 2, borderRadius: 2,
                    background: "linear-gradient(90deg, #6366F1, #8B5CF6, #EC4899)",
                    boxShadow: "0 0 8px rgba(139,92,246,0.7)",
                  }} />
                )}

                <span style={{ fontSize: 13 }}>{tab.icon || "📄"}</span>
                <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{tab.title}</span>

                {/* Close button */}
                <button
                  className="erp-tab-close"
                  onClick={(e) => { e.stopPropagation(); closeTab(tab.path); }}
                  title="Close tab"
                  style={{
                    background: "transparent", border: "none",
                    padding: 0, marginLeft: 2,
                    width: 16, height: 16, borderRadius: 3,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer",
                    color: active ? "#C7D2FE" : "#64748B",
                    opacity: active ? 0.85 : 0.55,
                    transition: "opacity 0.15s, background 0.15s, color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(220,38,38,0.4)";
                    e.currentTarget.style.color = "#fff";
                    e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = active ? "#C7D2FE" : "#64748B";
                    e.currentTarget.style.opacity = active ? "0.85" : "0.55";
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10">
                    <path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        {/* Right-side info */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "0 10px 0 8px", marginLeft: 4,
          fontSize: 10, color: "#475569",
          borderLeft: "1px solid rgba(99,102,241,0.18)",
          height: 24,
        }}>
          <span style={{ fontWeight: 700, color: "#6366F1" }}>{tabs.length}</span>
          <span>open</span>
        </div>
      </div>

      {/* Right-click menu */}
      {menu && (
        <div style={{
          position: "fixed",
          left: Math.min(menu.x, window.innerWidth - 180),
          top: Math.min(menu.y, window.innerHeight - 140),
          background: "#0F172A",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: 8,
          padding: 4,
          boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
          zIndex: 9999,
          minWidth: 170,
          animation: "tabSlideIn 0.15s ease-out",
        }}>
          {[
            { label: "Close", icon: "✕", fn: () => closeTab(menu.path), danger: true },
            { label: "Close Others", icon: "⟶", fn: () => closeOtherTabs(menu.path) },
            { label: "Close All", icon: "⎚", fn: closeAllTabs, danger: true },
          ].map((it, i) => (
            <div key={i}
              onClick={() => { it.fn(); setMenu(null); }}
              style={{
                padding: "7px 12px", borderRadius: 5,
                fontSize: 12, color: it.danger ? "#FCA5A5" : "#CBD5E1",
                cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
                transition: "background 0.12s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = it.danger ? "rgba(220,38,38,0.15)" : "rgba(99,102,241,0.18)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ width: 16, textAlign: "center", fontSize: 11 }}>{it.icon}</span>
              {it.label}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default TabBar;
